/**
 * pptxAnalyzer.ts
 *
 * Reads a .pptx (a zip archive) and reports where its size comes from: total
 * size, how much is media, which slides are the heaviest contributors, and
 * two actionable warnings (oversized images, unused slide layouts).
 *
 * Pure byte-in/data-out — no Office.js calls. The caller (useShareExport)
 * supplies the active presentation's bytes via officePptx.ts.
 */
import { strFromU8, unzipSync, type Unzipped } from "fflate";
import { logOfficeError } from "../../../../../telemetry";
import { resolveRelativePath, resolveRelationshipTarget } from "../../../utils/ooxmlRelationships";
import { round2 } from "../../../utils/formatUtils";
import type { ShareFileAnalysis, ShareIssue, SlideSizeEntry } from "../../../types";

/** Above this in either dimension, an image is flagged as "oversized" for
 * on-slide use — matches common PPTX-optimizer guidance (slides render at
 * well under 1500px on the long edge even on 4K displays). */
export const LARGE_IMAGE_THRESHOLD_PX = 1500;
/** Rough JPEG-recompression yield used only to label the warning's "could
 * save ~X" estimate — the real number is computed by pptxOptimizer once the
 * user actually runs it, so this is intentionally conservative, not a promise. */
export const ESTIMATED_SAVINGS_FACTOR = 0.45;
/** Rows shown in the "top contributors" breakdown. */
export const TOP_SLIDES_LIMIT = 5;

const IMAGE_MIME_BY_EXT: Record<string, string> = {
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png",
  gif: "image/gif", bmp: "image/bmp", webp: "image/webp",
};

export interface MediaEntry {
  path: string;
  bytes: Uint8Array;
  mimeType: string | null;
}

export interface ImageDimensions {
  width: number;
  height: number;
}

/** Injectable so tests can supply canned dimensions without a real decoder —
 * the default reads real pixel dimensions via the browser's Canvas API. */
export type ImageDimensionReader = (bytes: Uint8Array, mimeType: string) => Promise<ImageDimensions | null>;

/** Unzip a pptx into its parts. Throws a user-facing message on a corrupt/non-zip file. */
export function unzipPptx(buffer: ArrayBuffer): Unzipped {
  try {
    return unzipSync(new Uint8Array(buffer));
  } catch (err) {
    logOfficeError(err, "share-export-unzip");
    throw new Error("This file doesn't look like a valid .pptx (couldn't read it as a zip archive).");
  }
}

function sumEntrySizes(files: Unzipped, prefix: string): number {
  let total = 0;
  for (const [path, bytes] of Object.entries(files)) {
    if (path.startsWith(prefix)) total += bytes.byteLength;
  }
  return total;
}

function totalArchiveSize(files: Unzipped): number {
  return Object.values(files).reduce((sum, bytes) => sum + bytes.byteLength, 0);
}

/** `ppt/slides/slideN.xml` entries, sorted by their numeric N ascending — object key order isn't guaranteed to be numeric. */
function listSlidePaths(files: Unzipped): string[] {
  const slideRe = /^ppt\/slides\/slide(\d+)\.xml$/;
  return Object.keys(files)
    .map((path) => ({ path, match: slideRe.exec(path) }))
    .filter((entry): entry is { path: string; match: RegExpExecArray } => entry.match !== null)
    .sort((a, b) => Number(a.match[1]) - Number(b.match[1]))
    .map((entry) => entry.path);
}

function parseXml(xml: string): Document {
  return new DOMParser().parseFromString(xml, "text/xml");
}

/** First non-empty run of text on the slide, else a generic fallback — mirrors how the mockup labels rows ("Global footprint", "Agenda", ...). */
function extractSlideTitle(xml: string, slideIndex: number): string {
  try {
    const doc = parseXml(xml);
    const runs = doc.getElementsByTagName("a:t");
    for (const run of runs) {
      const text = run.textContent?.trim();
      if (text) return text.length > 40 ? `${text.slice(0, 40)}…` : text;
    }
  } catch (err) {
    logOfficeError(err, "share-export-slide-title");
  }
  return `Slide ${slideIndex}`;
}

function slideRelsPath(slidePath: string): string {
  const fileName = slidePath.split("/").pop() ?? "";
  return `ppt/slides/_rels/${fileName}.rels`;
}

function readXmlEntry(files: Unzipped, path: string): string | null {
  const bytes = files[path];
  return bytes ? strFromU8(bytes) : null;
}

/** Every image path a slide's relationships point at, resolved to zip-entry paths. */
function slideImagePaths(files: Unzipped, slidePath: string): string[] {
  const relsXml = readXmlEntry(files, slideRelsPath(slidePath));
  if (!relsXml) return [];
  const baseDir = "ppt/slides";
  const paths: string[] = [];
  try {
    const relsDoc = parseXml(relsXml);
    for (const rel of relsDoc.getElementsByTagName("Relationship")) {
      const type = rel.getAttribute("Type");
      const target = rel.getAttribute("Target");
      if (!type || !target || !type.endsWith("/image")) continue;
      paths.push(resolveRelativePath(baseDir, target));
    }
  } catch (err) {
    logOfficeError(err, "share-export-slide-images");
  }
  return paths;
}

/** Per-slide size = its own XML plus every image it references (an image shared by two slides is counted in full for each — a deliberate simplification: this is a "why is this slide heavy" view, not a strict partition of the archive). */
function computeSlideBreakdown(files: Unzipped, slidePaths: string[]): SlideSizeEntry[] {
  const entries: SlideSizeEntry[] = slidePaths.map((slidePath, i) => {
    const slideIndex = i + 1;
    const ownBytes = files[slidePath]?.byteLength ?? 0;
    const imageBytes = slideImagePaths(files, slidePath)
      .reduce((sum, imagePath) => sum + (files[imagePath]?.byteLength ?? 0), 0);
    const xml = readXmlEntry(files, slidePath) ?? "";
    return { slideIndex, title: extractSlideTitle(xml, slideIndex), sizeBytes: ownBytes + imageBytes };
  });
  entries.sort((a, b) => b.sizeBytes - a.sizeBytes);
  return entries.slice(0, TOP_SLIDES_LIMIT);
}

export function listMediaEntries(files: Unzipped): MediaEntry[] {
  const mediaRe = /^ppt\/media\/[^/]+\.([a-zA-Z0-9]+)$/;
  const entries: MediaEntry[] = [];
  for (const [path, bytes] of Object.entries(files)) {
    const match = mediaRe.exec(path);
    if (!match) continue;
    const ext = (match[1] ?? "").toLowerCase();
    entries.push({ path, bytes, mimeType: IMAGE_MIME_BY_EXT[ext] ?? null });
  }
  return entries;
}

/** Real pixel dimensions via the browser's native image decoder. Returns
 * null (rather than throwing) on anything undecodable — a single bad or
 * unusual image must not abort the whole analysis. */
export const readImageDimensionsViaBitmap: ImageDimensionReader = async (bytes, mimeType) => {
  try {
    const blob = new Blob([bytes as BlobPart], { type: mimeType });
    const bitmap = await createImageBitmap(blob);
    const dims = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return dims;
  } catch (err) {
    logOfficeError(err, "share-export-image-dimensions");
    return null;
  }
};

export interface LargeImageFindings {
  count: number;
  estimatedSavingsBytes: number;
}

/** `dimensionReader` is injectable so this stays unit-testable without a real
 * image decoder (jsdom has no createImageBitmap support). */
export async function detectLargeImages(
  mediaEntries: MediaEntry[],
  dimensionReader: ImageDimensionReader = readImageDimensionsViaBitmap,
): Promise<LargeImageFindings> {
  let count = 0;
  let estimatedSavingsBytes = 0;
  for (const entry of mediaEntries) {
    if (!entry.mimeType) continue; // not a recognised raster image format
    const dims = await dimensionReader(entry.bytes, entry.mimeType);
    if (!dims) continue;
    if (dims.width > LARGE_IMAGE_THRESHOLD_PX || dims.height > LARGE_IMAGE_THRESHOLD_PX) {
      count += 1;
      estimatedSavingsBytes += entry.bytes.byteLength * ESTIMATED_SAVINGS_FACTOR;
    }
  }
  return { count, estimatedSavingsBytes: Math.round(estimatedSavingsBytes) };
}

function relationshipTargetsByType(relsXml: string | null, baseDir: string, typeSuffix: string): string[] {
  if (!relsXml) return [];
  const targets: string[] = [];
  try {
    const doc = parseXml(relsXml);
    for (const rel of doc.getElementsByTagName("Relationship")) {
      const type = rel.getAttribute("Type");
      const target = rel.getAttribute("Target");
      if (!type || !target || !type.endsWith(typeSuffix)) continue;
      targets.push(resolveRelativePath(baseDir, target));
    }
  } catch (err) {
    logOfficeError(err, "share-export-relationship-targets");
  }
  return targets;
}

export interface UnusedLayoutFindings {
  totalLayouts: number;
  unusedPaths: string[];
}

/** A layout is "unused" when it's declared by a slide master but no slide's
 * own relationships point at it. Masters themselves are never removed —
 * only orphaned layouts. */
export function detectUnusedLayouts(files: Unzipped, slidePaths: string[]): UnusedLayoutFindings {
  const masterPaths = relationshipTargetsByType(
    readXmlEntry(files, "ppt/_rels/presentation.xml.rels"), "ppt", "/slideMaster",
  );

  const declaredLayouts = new Set<string>();
  for (const masterPath of masterPaths) {
    // A relationship Target is always resolved relative to the directory of
    // the part that OWNS the .rels file — here, the master's own directory,
    // not the layouts' directory the target happens to point into.
    const masterDir = masterPath.split("/").slice(0, -1).join("/");
    const masterRelsPath = `${masterDir}/_rels/${masterPath.split("/").pop()}.rels`;
    relationshipTargetsByType(readXmlEntry(files, masterRelsPath), masterDir, "/slideLayout")
      .forEach((path) => declaredLayouts.add(path));
  }

  const usedLayouts = new Set<string>();
  for (const slidePath of slidePaths) {
    const target = resolveRelationshipTarget(
      readXmlEntry(files, slideRelsPath(slidePath)),
      (type) => type.endsWith("/slideLayout"),
      "ppt/slides",
    );
    if (target) usedLayouts.add(target);
  }

  const unusedPaths = [...declaredLayouts].filter((path) => !usedLayouts.has(path));
  return { totalLayouts: declaredLayouts.size, unusedPaths };
}

function buildIssues(large: LargeImageFindings, unused: UnusedLayoutFindings): ShareIssue[] {
  const issues: ShareIssue[] = [];
  if (large.count > 0) {
    const savingsMb = round2(large.estimatedSavingsBytes / (1024 * 1024));
    issues.push({
      kind: "largeImages",
      count: large.count,
      message: `${large.count} image${large.count === 1 ? " is" : "s are"} larger than ${LARGE_IMAGE_THRESHOLD_PX}px — compressing could save ~${savingsMb}MB.`,
    });
  }
  if (unused.unusedPaths.length > 0) {
    const n = unused.unusedPaths.length;
    issues.push({
      kind: "unusedLayouts",
      count: n,
      message: `${n} unused slide layout${n === 1 ? "" : "s"} can be removed.`,
    });
  }
  return issues;
}

/**
 * Analyse a pptx's bytes and report size breakdown + actionable issues.
 * `dimensionReader` is exposed only for tests; production callers should omit it.
 */
export async function analyzePresentationBytes(
  buffer: ArrayBuffer,
  dimensionReader: ImageDimensionReader = readImageDimensionsViaBitmap,
): Promise<ShareFileAnalysis> {
  const files = unzipPptx(buffer);
  const slidePaths = listSlidePaths(files);
  const mediaEntries = listMediaEntries(files);

  const totalSizeBytes = totalArchiveSize(files);
  const mediaSizeBytes = sumEntrySizes(files, "ppt/media/");
  const mediaPercent = totalSizeBytes > 0 ? round2((mediaSizeBytes / totalSizeBytes) * 100) : 0;

  const [largeImages, unusedLayouts] = await Promise.all([
    detectLargeImages(mediaEntries, dimensionReader),
    Promise.resolve(detectUnusedLayouts(files, slidePaths)),
  ]);

  return {
    totalSizeBytes,
    slideCount: slidePaths.length,
    mediaSizeBytes,
    mediaPercent,
    topSlides: computeSlideBreakdown(files, slidePaths),
    issues: buildIssues(largeImages, unusedLayouts),
  };
}
